import {useEffect, useState} from 'react'
import {publish, subscribe, unsubscribe} from './event'
import {sharedEventBus} from './sharedEventBus'
import {local_storage, session_storage} from './storage'

const EVENT_CHANGE = 'signal_change'
const $signal = Symbol('signal')

export const cacheSignal = (signal, key, persistent = false) => {
    const clearCache = () => {
        sessionStorage.removeItem(key)
        localStorage.removeItem(key)
    }

    ;(() => {
        const cachedValue = session_storage.load(key)

        if (void 0 !== cachedValue) {
            signal.value = cachedValue
            return
        }

        if (persistent) {
            const cachedValue = local_storage.load(key)

            if (void 0 !== cachedValue) {
                signal.value = cachedValue
            }
        }
    })()

    watch(
        () => {
            session_storage.save(key, signal.value)

            if (persistent) {
                local_storage.save(key, signal.value)
            }
        },

        [signal]
    )

    return clearCache
}

export const sharedSignal = (init, key) => {
    const [sig, useValue] = signal(init)

    const shared = {
        get value() {
            return sig.value
        },

        set value(newValue) {
            if (sig.value !== newValue) {
                sig.value = newValue
                sharedEventBus.publish(key, newValue)
            }
        },

        [$signal]: sig
    }

    sharedEventBus.subscribe(key, value => sig.value = value)
    return [shared, useValue]
}

export const signal = init => {
    let value = init

    const ref = {
        get value() {
            return value
        },

        set value(newValue) {
            if (value !== newValue) {
                value = newValue
                publish(this, EVENT_CHANGE, value)
            }
        },
    }

    const useValue = () => {
        const [state, setState] = useState(value)

        useEffect(
            () => {
                subscribe(ref, EVENT_CHANGE, setState)
                return () => unsubscribe(ref, EVENT_CHANGE, setState)
            },

            []
        )

        return state
    }

    return [ref, useValue]
}

export const useWatch = (fn, refs) => {
    useEffect(
        () => {
            const cleanup = watch(fn, refs)
            return () => cleanup.value?.()
        },

        []
    )
}

export const watch = (fn, refs) => {
    let pending = false
    const cleanup = {value: fn()}

    // 多个依赖改变时，回调只执行一次
    const _fn = () => {
        if (pending) {
            return
        }

        cleanup.value?.()
        pending = true

        queueMicrotask(() => {
            pending = false
            cleanup.value = fn()
        })
    }

    for (const ref of refs) {
        const source = ref[$signal] ?? ref
        subscribe(source, EVENT_CHANGE, _fn)
    }

    return cleanup
}
